import { z } from 'zod'
import {
  AreasOfInterestEnumValues,
  BaseLayerTypeEnumValues,
  MarketMakerRequestStatusEnumValues,
  SectorTypeEnumValues,
} from '@forgd/shared/enums'
import { FORGD_SYSTEM_COINGECKO_ID } from '@forgd/shared/constants'

export const TProject = z.object({
  id: z.string().uuid(),
  hasToken: z.boolean(),
  isResearch: z.boolean(),
  tokenListed: z.boolean(),
  name: z.string(),
  ticker: z.string().nullable(),
  coingeckoId: z.string().nullable(),
  image: z.string().nullable(),
  link: z.string().nullable(),
  organizationId: z.string().uuid().nullable().optional(),
  tgeDate: z.string().date().nullable(),
  areasOfInterest: z.array(z.enum(AreasOfInterestEnumValues)).nullable().optional(),
  sector: z.array(z.enum(SectorTypeEnumValues)).nullable().optional(),
  baseLayer: z.array(z.enum(BaseLayerTypeEnumValues)).nullable().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable().optional(),
})

export const TUpdateProjectBody = z.object({
  hasToken: z.boolean().optional(),
  tokenListed: z.boolean().optional(),
  name: z.string().trim().min(1).optional(),
  ticker: z
    .string()
    .trim()
    .min(1)
    .transform((val) => val.toUpperCase())
    .optional(),
  coingeckoId: z
    .string()
    .nullable()
    .optional()
    .refine((val) => val?.trim() !== FORGD_SYSTEM_COINGECKO_ID, 'Coingecko cannot be system token'),
  image: z.string().url().optional().nullable(),
  link: z.string().url().optional(),
  organizationId: z.string().uuid().optional(),
  tgeDate: z.string().date().optional(),
  areasOfInterest: z.array(z.enum(AreasOfInterestEnumValues)).nullable().optional(),
  sector: z.array(z.enum(SectorTypeEnumValues)).nullable().optional(),
  baseLayer: z.array(z.enum(BaseLayerTypeEnumValues)).nullable().optional(),
})

export const TProjects = z.object({
  items: z.array(TProject),
})

const TokenPrices = z.object({
  price: z.number().safe(),
  priceChangePercentage24h: z.number().safe(),
  marketChartPrice24h: z.array(
    z.object({
      date: z.number().safe(),
      value: z.number().safe(),
    }),
  ),
})

export type TokenPrices = z.infer<typeof TokenPrices>

const MmReceivedQuotes = z.object({
  round1: z.number().safe(),
  round2: z.number().safe(),
})

export type MmReceivedQuotes = z.infer<typeof MmReceivedQuotes>

const DashboardStatsExchange = z.object({
  name: z.string(),
  logoUrl: z.string().nullable(),
})

const DashboardStatsPreTge = z.object({
  isTokenListed: z.literal(false),
  tgeDate: z.string().date().nullable(),
  isResearch: z.boolean(),
  projectTicker: z.string().nullable(),
  isPublicPagePublished: z.boolean(),
  mmRfqStatus: z.enum(MarketMakerRequestStatusEnumValues),
  mmReceivedQuotes: MmReceivedQuotes,
  mmEngagements: z.array(
    z.object({
      id: z.string(),
      imageUrl: z.string().nullable(),
    }),
  ),
  mmRfqLastReportedAt: z.string().date().nullable(),
  activeMembers: z.number().safe(),
  advisor: z
    .object({
      organizationName: z.string(),
      imageUrl: z.string().nullable(),
    })
    .nullable(),
})

const DashboardStatsPostTge = z.object({
  isTokenListed: z.literal(true),
  projectTicker: z.string().nullable(),
  tokenPrices: TokenPrices.optional(),
  exchanges: z.object({
    cex: z.number().safe(),
    dex: z.number().safe(),
    top: z.array(DashboardStatsExchange),
  }),
  circulatingSupply: z.number().safe().nullable(),
  volume24h: z.number().safe().nullable(),
  depth2Pct: z.number().safe().nullable(),
  mmRfqStatus: z.enum(MarketMakerRequestStatusEnumValues),
  mmReceivedQuotes: MmReceivedQuotes,
  mmEngagements: z.array(
    z.object({
      id: z.string(),
      imageUrl: z.string().nullable(),
    }),
  ),
  mmRfqLastReportedAt: z.string().date().nullable(),
})

export const ProjectDashboardStatsResponse = z.discriminatedUnion('isTokenListed', [
  DashboardStatsPreTge,
  DashboardStatsPostTge,
])

export type ProjectDashboardStats = z.infer<typeof ProjectDashboardStatsResponse>
